<template>
	<SettingsItemLayout>
		<template v-slot:bar>
			<v-tooltip top>
				<template v-slot:activator="{ on }">
					<v-btn
						v-on="on"
						icon
						:color="`${isDark ? 'grey lighten-3' : 'grey darken-3'}`"
						@click="handleRefresh"
					>
						<v-icon>refresh</v-icon>
					</v-btn>
				</template>
				{{ $t('tooltip.refresh') }}
			</v-tooltip>
		</template>
		<template v-slot:navigation>
			<v-list dense>
				<v-list-item
					@click="handleOpenScorecardTemplate({ id: null })"
					:class="scorecardTemplate.id === null ? 'v-list-item--active' : ''"
				>
					<v-list-item-icon>
						<v-icon>add</v-icon>
					</v-list-item-icon>
					<v-list-item-content>
						<v-list-item-title>{{
							$t('button.new-scorecard-template')
						}}</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
				<v-list-item
					@click="handleOpenScorecardTemplate({ id: item.id })"
					v-for="(item, index) in scorecardTemplates"
					:key="index"
					:class="scorecardTemplate.id === item.id ? 'v-list-item--active' : ''"
				>
					<v-list-item-icon>
						<v-badge
							v-if="!item.addButton"
							bottom
							:color="item.active ? 'green' : 'grey'"
							dot
							offset-x="10"
							offset-y="10"
						>
							<v-icon>folder_open</v-icon>
						</v-badge>
					</v-list-item-icon>
					<v-list-item-content>
						<v-list-item-title>{{ item.label }}</v-list-item-title>
					</v-list-item-content>
				</v-list-item>
			</v-list>
		</template>
		<template>
			<v-card tile elevation="0">
				<v-toolbar :color="`${isDark ? '' : 'grey lighten-3'}`" flat dense>
					<v-toolbar-title>
						{{ scorecardTemplate.label }}
					</v-toolbar-title>
					<v-spacer></v-spacer>

					<v-btn-toggle
						rounded
						dense
						v-model="viewMode"
						style="margin-right: 0.5rem"
					>
						<v-btn value="edit">
							<v-icon left>edit</v-icon>
							{{ $t('button.edit') }}
						</v-btn>
						<v-btn value="preview">
							<v-icon left>visibility</v-icon>
							{{ $t('button.preview') }}
						</v-btn>
					</v-btn-toggle>

					<v-btn @click="handleSubmit" color="primary" :disabled="previewMode">
						{{ $t('button.save-changes') }}
					</v-btn>
				</v-toolbar>
				<v-card-text v-if="!previewMode">
					<ValidationObserver
						ref="dynamic_form_build"
						tag="form"
						@submit="handleSubmit"
						id="dynamic_form_build"
					>
						<v-container>
							<v-row>
								<v-col>
									<fieldset
										class="talentics-fieldset talentics-fieldset-paddings"
									>
										<legend>
											{{ $t('label.status') }}:
											{{
												scorecardTemplate.active
													? $t('label.active')
													: $t('label.inactive')
											}}
										</legend>
										<v-switch
											v-model="scorecardTemplate.active"
											color="success"
											class="v-input--template-switch"
											hide-details
											inset
										></v-switch>
									</fieldset>
								</v-col>
							</v-row>

							<v-row>
								<v-col>
									<ValidationProvider rules="required" v-slot="{ errors }">
										<v-text-field
											v-model="scorecardTemplate.label"
											:label="$t('label.label')"
											outlined
											dense
											hide-details
											:error-messages="errors"
										></v-text-field>
									</ValidationProvider>
								</v-col>
							</v-row>

							<v-row>
								<v-col>
									<v-divider></v-divider>
								</v-col>
							</v-row>

							<v-row>
								<v-col>
									<ValidationProvider rules="required" v-slot="{ errors }">
										<v-text-field
											v-model="scorecardTemplate.title"
											:label="$t('label.title-visible-by-interviewers')"
											outlined
											dense
											hide-details
											:error-messages="errors"
										></v-text-field>
									</ValidationProvider>
								</v-col>
							</v-row>

							<v-row>
								<v-col>
									<fieldset
										class="talentics-fieldset talentics-fieldset-paddings"
									>
										<legend>
											{{ $t('label.instructions-for-interviewers') }}
										</legend>
										<RichEditor
											v-model="scorecardTemplate.instructions"
											refName="instructions"
										></RichEditor>
									</fieldset>
								</v-col>
							</v-row>

							<v-row>
								<v-col>
									<fieldset
										class="talentics-fieldset talentics-fieldset-paddings"
									>
										<legend>
											{{ $t('label.form-items') }}
										</legend>
										<v-container>
											<v-row v-if="scorecardTemplate.json_schema.length > 0">
												<v-col>
													<DynamicForm
														:jsonSchema="scorecardTemplate.json_schema"
														:contextSchema="scorecardTemplate.context_schema"
														:onChange="handleDynamicFormChange"
														:onDelete="handleDeleteFormItem"
														:onMove="handleDynamicFormMove"
													></DynamicForm>
												</v-col>
											</v-row>
											<v-row>
												<v-col cols="9">
													<v-select
														v-model="newFormItem"
														outlined
														hide-details
														dense
														:items="[
															{
																value: null,
																text: $t('label.please-select'),
															},
															{
																value: 'scorecardSliderItemBuilder',
																text: $t('text.scorecard-slider-uppercase'),
															},
															{
																value: 'scorecardDropdownItemBuilder',
																text: $t('text.scorecard-dropdown-uppercase'),
															},
															{
																value: 'scorecardRadioItemBuilder',
																text: $t('text.scorecard-radio-uppercase'),
															},
														]"
													></v-select>
												</v-col>
												<v-col cols="3">
													<v-btn
														block
														@click="handleAddNewFormItem"
														:disabled="Boolean(!newFormItem)"
													>
														{{ $t('button.add') }}
													</v-btn>
												</v-col>
											</v-row>
										</v-container>
									</fieldset>
								</v-col>
							</v-row>

							<v-row>
								<v-col>
									<ValidationProvider
										:rules="{ required: true, is: 100 }"
										v-slot="{ errors }"
									>
										<v-text-field
											v-model="totalWeight"
											:label="$t('label.total-weight')"
											outlined
											hide-details
											:error-messages="errors"
											readonly
											dense
										></v-text-field>
									</ValidationProvider>
									<div style="margin-top: 1rem">
										<v-alert :type="totalWeight === 100 ? 'success' : 'error'"
											>Total weight must be 100</v-alert
										>
									</div>
								</v-col>
							</v-row>
						</v-container>
					</ValidationObserver>
				</v-card-text>
				<v-card-text v-else>
					<ValidationObserver
						ref="dynamic_form_preview"
						tag="form"
						@submit="handleSubmit"
						id="dynamic_form_preview"
					>
						<v-container>
							<v-row
								v-if="
									scorecardTemplate.instructions !== '<p><br></p>' &&
									scorecardTemplate.instructions !== undefined
								"
							>
								<v-col v-html="sanitize(scorecardTemplate.instructions)"></v-col>
							</v-row>
							<v-row>
								<v-col>
									<DynamicForm
										:jsonSchema="
											completedScorecardTemplate.json_schema.filter(
												item => !item.props,
											)
										"
										:contextSchema="completedScorecardTemplate.context_schema"
										:onChange="handleDynamicFormPreviewChange"
									></DynamicForm>
								</v-col>
							</v-row>
						</v-container>
					</ValidationObserver>
				</v-card-text>
			</v-card>
		</template>
	</SettingsItemLayout>
</template>

<script>
import { mapState } from 'vuex';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import DynamicForm from '@/components/shared/dynamicForm/DynamicForm';
import RichEditor from '@/components/shared/richEditor/RichEditor';
import SettingsItemLayout from '../settingsItemLayout/SettingsItemLayout';
import { sanitize } from '@/utils/sanitize';

export default {
	name: 'ScorecardTemplates',
	components: {
		ValidationObserver,
		ValidationProvider,
		DynamicForm,
		RichEditor,
		SettingsItemLayout,
	},
	computed: {
		...mapState({
			users: state => state.users.items,
			scorecardTemplates: state => state.scorecardTemplates.items,
			isDark: state => state.currentUser.isDark,
		}),
		totalWeightComputed() {
			if (this.scorecardTemplate.context_schema) {
				const keys = Object.keys(this.scorecardTemplate.context_schema);
				return keys.reduce((acc, key) => {
					if (this.scorecardTemplate.context_schema[key]) {
						const { weight } = this.scorecardTemplate.context_schema[key];
						return weight
							? acc +
									parseInt(
										this.scorecardTemplate.context_schema[key].weight,
										10,
									)
							: acc;
					}
					return acc;
				}, 0);
			}
			return 0;
		},
	},
	data() {
		return {
			showTemplateDetails: false,
			previewMode: false,
			scorecardTemplate: {
				id: null,
				label: '',
				json_schema: [],
				context_schema: {},
				active: true,
			},
			newFormItem: null,
			completedScorecardTemplate: {},
			viewMode: 'edit',
			totalWeight: 0,
		};
	},
	methods: {
		sanitize,
		handleClose() {
			this.viewMode = 'edit';
			this.showTemplateDetails = false;
		},
		async handleSubmit(evt) {
			if (evt) {
				evt.preventDefault();
			}
			const isValid = await this.$refs.dynamic_form_build.validate();
			if (isValid) {
				const { scorecardTemplate } = this;
				const { id } = scorecardTemplate;
				if (id) {
					this.$store.dispatch('scorecardTemplates/put', {
						items: [
							{
								...scorecardTemplate,
								created_json_schema: this.getCreatedJsonSchema(),
							},
						],
					});
				} else {
					this.$store
						.dispatch('scorecardTemplates/post', {
							items: [
								{
									...scorecardTemplate,
									created_json_schema: this.getCreatedJsonSchema(),
								},
							],
						})
						.then(response => {
							const { items } = response.data;
							if (items.length > 0) {
								const { id } = items[0];
								this.handleOpenScorecardTemplate({ id });
							}
						});
				}
			}
		},
		handleOpenScorecardTemplate({ id }) {
			if (id) {
				this.$store
					.dispatch('scorecardTemplates/get', { id, refresh: true })
					.then(response => {
						const { items } = response.data;
						if (items.length > 0) {
							this.scorecardTemplate = { ...items[0] };
							this.showTemplateDetails = true;
							this.viewMode = 'edit';
							this.previewMode = false;
						}
					});
			} else {
				this.scorecardTemplate = {
					id: null,
					label: '',
					json_schema: [],
					context_schema: {},
					active: true,
				};
				this.showTemplateDetails = true;
				this.viewMode = 'edit';
				this.previewMode = false;
			}
		},
		handleAddNewFormItem() {
			const { newFormItem } = this;
			const rand = Math.floor(100000 + Math.random() * 9000000);
			this.scorecardTemplate = {
				...this.scorecardTemplate,
				json_schema: [
					...this.scorecardTemplate.json_schema,
					{
						type: newFormItem,
						paramName: `param_${rand}`,
					},
				],
				context_schema: {
					...this.scorecardTemplate.context_schema,
					[`param_${rand}`]: {
						type: newFormItem,
					},
				},
			};
			this.newFormItem = null;
		},
		handleDeleteFormItem({ formItemIndex, formItemKey }) {
			this.scorecardTemplate = {
				...this.scorecardTemplate,
				json_schema: this.scorecardTemplate.json_schema.filter(
					(item, index) => index !== formItemIndex,
				),
				context_schema: {
					...this.scorecardTemplate.context_schema,
					[formItemKey]: undefined,
				},
			};
		},
		handleDynamicFormChange({ newVal }) {
			this.scorecardTemplate = {
				...this.scorecardTemplate,
				context_schema: {
					...this.scorecardTemplate.context_schema,
					...newVal,
				},
			};
		},
		handleDynamicFormPreviewChange({ newVal }) {
			this.completedScorecardTemplate = {
				...this.completedScorecardTemplate,
				context_schema: {
					...this.completedScorecardTemplate.context_schema,
					...newVal,
				},
			};
		},
		handleDynamicFormMove({ newVal }) {
			this.scorecardTemplate = {
				...this.scorecardTemplate,
				json_schema: [...newVal],
			};
		},
		getCreatedJsonSchema() {
			const { json_schema, context_schema, title, instructions } =
				this.scorecardTemplate;
			return [
				...json_schema.map(item => {
					const contextItem = context_schema[item.paramName];
					return {
						...item,
						...contextItem,
						type: item.type.substring(0, item.type.length - 7),
						required: true,
						maxLength: contextItem.maxLength && parseInt(contextItem.maxLength),
						options: contextItem.options
							? contextItem.options.map(item => {
									return {
										value: parseInt(item.optionValue, 10),
										label: item.optionLabel,
									};
							  })
							: undefined,
					};
				}),
				{
					props: {
						title,
						instructions,
					},
				},
			];
		},
		handleRefresh() {
			this.$store.dispatch('scorecardTemplates/get', {
				updateStore: true,
				refresh: true,
			});
			this.handleOpenScorecardTemplate({ id: null });
		},
	},
	created() {
		this.$store.dispatch('scorecardTemplates/get', { updateStore: true });
		this.showTemplateDetails = false;
	},
	mounted() {
		this.handleOpenScorecardTemplate({ id: null });
	},
	watch: {
		viewMode: {
			handler: async function (newVal) {
				if (newVal === 'preview') {
					const isValid = await this.$refs.dynamic_form_build.validate();
					if (isValid) {
						const json_schema = this.getCreatedJsonSchema();
						this.completedScorecardTemplate = {
							...this.scorecardTemplate,
							json_schema,
							context_schema: {},
						};
						this.previewMode = true;
					} else {
						this.viewMode = 'edit';
						this.previewMode = false;
					}
				} else {
					this.previewMode = false;
				}
			},
			deep: true,
			immediate: false,
		},
		totalWeightComputed: {
			handler: function (newVal) {
				this.totalWeight = newVal;
			},
			deep: true,
			immediate: true,
		},
	},
};
</script>
